import { useState, type FormEvent } from "react";
import { ApiError } from "@/api/client";
import {
  useCreateUser,
  useMeta,
  useSession,
  useSetUserPassword,
  useUpdateUser,
  useUsers,
  type UserAccount,
} from "@/api/queries";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Chip } from "@/components/ui/Chip";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Field } from "@/components/ui/Field";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { Spinner } from "@/components/ui/Spinner";
import { KeyIcon, PlusIcon } from "@/components/icons";
import { relativeTime } from "@/lib/time";

/**
 * Named accounts (ACC-3, PRD FR-10.2).
 *
 * Optional: with none, this deployment unlocks by key alone. Once one exists,
 * the unlock page offers to sign in by name, and every `actor` column the
 * person's work touches carries their username.
 *
 * There is no delete, on purpose. An author row outlives its author, so
 * leaving is **Disable** — which also signs the account out everywhere on its
 * next request. A new password does the same. Both ask first, and say so.
 */

type Role = "admin" | "analyst";

function errorText(error: unknown) {
  return error instanceof ApiError ? error.message : null;
}

function CreateUser() {
  const create = useCreateUser();
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<Role>("analyst");
  const [password, setPassword] = useState("");

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    create.mutate(
      { username, display_name: displayName, role, password },
      {
        onSuccess: () => {
          setUsername("");
          setDisplayName("");
          setPassword("");
          setRole("analyst");
        },
      },
    );
  };

  return (
    <Card>
      <CardHeader
        title="Add a person"
        description="They sign in with this username and password. Share the password with them directly — it is never shown again."
      />
      <CardBody>
        <form className="grid gap-4 sm:grid-cols-2" onSubmit={submit}>
          <Field
            label="Username"
            autoComplete="off"
            spellCheck={false}
            autoCapitalize="none"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            hint="Lowercase letters, digits, dots, dashes. It cannot be changed later."
          />
          <Field
            label="Display name"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
          />
          <Select label="Role" value={role} onChange={(event) => setRole(event.target.value as Role)}>
            <option value="analyst">Analyst — uses the product</option>
            <option value="admin">Admin — governs the deployment</option>
          </Select>
          <Field
            label="Password"
            revealable
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            hint="At least 10 characters."
          />
          <div className="flex items-center gap-3 sm:col-span-2">
            <Button
              type="submit"
              variant="primary"
              icon={<PlusIcon className="size-4" />}
              loading={create.isPending}
              disabled={!username.trim() || !password}
            >
              Add person
            </Button>
            {create.isError ? (
              <span className="type-support text-error">{errorText(create.error)}</span>
            ) : create.isSuccess ? (
              <span className="type-support text-success">Added</span>
            ) : null}
          </div>
        </form>
      </CardBody>
    </Card>
  );
}

function PasswordDialog({ user, onClose }: { user: UserAccount | null; onClose: () => void }) {
  const set = useSetUserPassword();
  const [password, setPassword] = useState("");
  const close = () => {
    setPassword("");
    set.reset();
    onClose();
  };
  return (
    <Modal
      open={Boolean(user)}
      onClose={close}
      title={`New password for ${user?.display_name ?? ""}`}
      description="Setting a new password also signs them out everywhere, on their next click."
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={set.isPending}
            disabled={!password}
            onClick={() => {
              if (!user) return;
              set.mutate({ id: user.id, password }, { onSuccess: close });
            }}
          >
            Set password
          </Button>
        </>
      }
    >
      <Field
        label="New password"
        revealable
        autoFocus
        autoComplete="new-password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        error={set.isError ? errorText(set.error) : undefined}
        hint="At least 10 characters. Share it with them directly."
      />
    </Modal>
  );
}

function UserRow({
  user,
  me,
  locale,
  onPassword,
  onDisable,
}: {
  user: UserAccount;
  me: boolean;
  locale: string;
  onPassword: () => void;
  onDisable: () => void;
}) {
  const update = useUpdateUser();
  return (
    <li className="flex flex-wrap items-center gap-3 px-4 py-3">
      <div className="min-w-48 flex-1">
        <p className="type-label text-ink">
          {user.display_name}
          {me ? <span className="text-ink-disabled"> (you)</span> : null}
        </p>
        <p className="type-caption text-ink-disabled normal-case">
          {user.username}
          {" · "}
          {user.last_login_at
            ? `signed in ${relativeTime(user.last_login_at, locale)}`
            : "never signed in"}
        </p>
      </div>
      {user.disabled ? <Chip tone="neutral">disabled</Chip> : null}
      <select
        aria-label={`Role for ${user.username}`}
        value={user.role}
        disabled={me || update.isPending}
        title={me ? "Another administrator has to change your role." : undefined}
        onChange={(event) => update.mutate({ id: user.id, role: event.target.value as Role })}
        className="control-sm rounded-element bg-card text-ink type-support border-border border px-2"
      >
        <option value="analyst">Analyst</option>
        <option value="admin">Admin</option>
      </select>
      <Button size="sm" icon={<KeyIcon className="size-3.5" />} onClick={onPassword}>
        New password
      </Button>
      {user.disabled ? (
        <Button
          size="sm"
          loading={update.isPending}
          onClick={() => update.mutate({ id: user.id, disabled: false })}
        >
          Enable
        </Button>
      ) : (
        <Button size="sm" variant="danger" disabled={me} onClick={onDisable}>
          Disable
        </Button>
      )}
    </li>
  );
}

export function UsersPage() {
  const { data: users, isLoading } = useUsers();
  const { data: session } = useSession();
  const { data: meta } = useMeta();
  const update = useUpdateUser();
  const [resetting, setResetting] = useState<UserAccount | null>(null);
  const [disabling, setDisabling] = useState<UserAccount | null>(null);

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-8 sm:py-10">
      <header className="mb-8">
        <p className="type-caption text-ink-disabled">Admin</p>
        <h1 className="type-h1 text-ink mt-2">People</h1>
        <p className="type-body text-ink-secondary mt-3 max-w-2xl">
          Optional named accounts. With none, everybody unlocks with a key. With some, people sign
          in by name and the audit shows who did what. The keys keep working beside them, for API
          clients above all.
        </p>
      </header>

      <div className="flex flex-col gap-5">
        <CreateUser />
        <Card>
          <CardHeader title="Accounts" description="Never deleted — disable someone who leaves." />
          <CardBody className="px-0 pb-0">
            {isLoading ? (
              <div className="px-5 pb-5">
                <Spinner />
              </div>
            ) : users && users.length ? (
              <ul className="divide-border border-border divide-y border-t">
                {users.map((user) => (
                  <UserRow
                    key={user.id}
                    user={user}
                    me={session?.user?.id === user.id}
                    locale={meta?.locale ?? "en-US"}
                    onPassword={() => setResetting(user)}
                    onDisable={() => setDisabling(user)}
                  />
                ))}
              </ul>
            ) : (
              <p className="type-support text-ink-secondary px-5 pb-5">
                No accounts yet. Until there is one, the unlock page asks only for a key.
              </p>
            )}
          </CardBody>
        </Card>
      </div>

      <PasswordDialog user={resetting} onClose={() => setResetting(null)} />
      <ConfirmDialog
        open={Boolean(disabling)}
        onClose={() => setDisabling(null)}
        loading={update.isPending}
        title={`Disable ${disabling?.display_name ?? ""}?`}
        description={<>{disabling?.username} will not be able to sign in.</>}
        consequence={
          <>
            Every session they hold ends on their next click. Their reports, schedules and audit
            history stay, under their name. You can enable the account again later.
          </>
        }
        confirmLabel="Disable"
        onConfirm={() => {
          if (!disabling) return;
          update.mutate(
            { id: disabling.id, disabled: true },
            { onSuccess: () => setDisabling(null) },
          );
        }}
      />
    </div>
  );
}
